import { useEffect, useState } from 'react'
import { LayoutList, Mail, CircleAlert, Clock, Power, PowerOff } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { useAddIncidentUpdate, useGetIncidentTimeline } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { LOCATION_OPTIONS } from '../../utils/fleet'
import {
  type IncidentRow, type TriStateValue,
  INCIDENT_STATUSES, incidentStatusLabel, triStatePayload, zar,
} from '../../utils/servicing'
import { TriState, NotifyBox } from './IncidentControls'
import {
  type TimelineRow, type InChargeOption, type IncidentUpdatePolicy, type IncidentCard,
  dayLabel, dueSentence, entryHeading, nextUpdateBounds, offeringSentence,
  onlineLabel, orderTimeline, updateTimingSentence,
} from './incidentTimeline'

/**
 * One car's incident card (migration 1601), and how to add to it.
 *
 * A card is the car, not an incident. It reads every incident the car has ever
 * had, oldest first: each report, every update, every R50 raised for a missed
 * date, each time the car went online or offline, and when the next update is
 * owed and by whom (v_incident_timeline). A new incident is still its own record
 * and still goes to the owner as its own report; it simply lands on this card.
 *
 * The form posts to one open incident, because the clock stays per incident
 * (Luyanda, 1 October 2026). After the fold every car had at most one open, so
 * the chooser only appears when a second is logged while the first is still open.
 *
 * Before incident_updates existed, every repair note inserted a whole new
 * incident, so a car that went to the panel beater and came back read as four
 * unrelated incidents. On 1 October 2026 MF17LKGP had 19, 13 of them open.
 *
 * Every owner report ends "We will keep you posted about this incident moving
 * forward". The notify switch here is how that promise is kept, and it defaults
 * off because most updates are internal working notes rather than news an owner
 * needs.
 */
export function IncidentCardDialog({
  card,
  openIncidents,
  canEdit = true,
  onSaved,
}: {
  card: IncidentCard
  // The car's open incidents, the ones an update can be posted to. Empty when
  // nothing is open on the car.
  openIncidents: IncidentRow[]
  // Anybody who can see incidents can read the card; only fleet editors get the
  // form under it, the same line add_incident_update_v1 draws.
  canEdit?: boolean
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  // Earliest owed first, so the default target is the one most likely to cost R50.
  const sortedOpen = [...openIncidents].sort((a, b) =>
    String(a.next_update_date ?? '9999').localeCompare(String(b.next_update_date ?? '9999')),
  )
  const [targetId, setTargetId] = useState('')
  const incident = sortedOpen.find((r) => r.incident_id === targetId) ?? sortedOpen[0] ?? null
  const add = useAddIncidentUpdate()
  const feed = useGetIncidentTimeline()
  const feedTrigger = feed.trigger

  const [note, setNote] = useState('')
  const [status, setStatus] = useState(incident?.status ?? 'open')
  const [operational, setOperational] = useState<TriStateValue>('unset')
  const [currentLocation, setCurrentLocation] = useState('')
  const [moveTo, setMoveTo] = useState('')
  const [outcome, setOutcome] = useState('')
  const [cost, setCost] = useState('')
  const [nextUpdate, setNextUpdate] = useState('')
  const [inCharge, setInCharge] = useState('')
  const [notify, setNotify] = useState(false)

  useEffect(() => {
    if (open) feedTrigger({ vehicle_id: card.vehicle_id }, { skipCache: true })
  }, [open, feedTrigger, card.vehicle_id])

  const fed = feed.data as
    | { timeline: TimelineRow[]; people: InChargeOption[]; policy: IncidentUpdatePolicy | null }
    | undefined
  const timeline = orderTimeline(fed?.timeline ?? [])
  const people = fed?.people ?? []
  const bounds = nextUpdateBounds(fed?.policy ?? null)
  const closing = status === 'resolved' || status === 'closed'
  // An open incident always has one person in charge. When it has none yet, the
  // picker is required, and add_incident_update_v1 refuses without it.
  const needsInCharge = !closing && !!incident && !incident.assigned_person_id

  // The furthest allowed date is the default, so posting an update never
  // silently promises less time than the rule gives.
  const boundsMax = bounds?.max
  useEffect(() => {
    if (open && boundsMax && nextUpdate === '') setNextUpdate(boundsMax)
  }, [open, boundsMax, nextUpdate])

  function reset() {
    setTargetId('')
    setNote('')
    setStatus(sortedOpen[0]?.status ?? 'open')
    setOperational('unset')
    setCurrentLocation('')
    setMoveTo('')
    setOutcome('')
    setCost('')
    setNextUpdate('')
    setInCharge('')
    setNotify(false)
  }

  function nullIfEmpty(v: string): string | null {
    const t = v.trim()
    return t === '' ? null : t
  }

  // The owner is told only when the router says so. Mirrors
  // incident_report_route_v1: historic model, owner not yet moved to Manage My
  // Car (migration 9100), AND an owner with an address.
  const ownerWillBeNotified =
    incident?.commercial_model === 'historic' && !incident?.incidents_internal_since && !!incident?.owner_email

  const dateOutOfRange =
    !closing && !!bounds && nextUpdate !== '' && (nextUpdate < bounds.min || nextUpdate > bounds.max)
  const canSave =
    !!incident && note.trim() !== '' && !add.loading && !(needsInCharge && inCharge === '') && !dateOutOfRange

  async function handleSave() {
    if (!incident) return
    const op = triStatePayload(operational)
    // Every key is sent, null where untouched. add_incident_update_v1 coalesces
    // a null to the value already on the incident, so a note-only update cannot
    // blank a cost or a location somebody recorded yesterday.
    const update: Record<string, unknown> = {
      note: note.trim(),
      status,
      current_location: nullIfEmpty(currentLocation),
      move_to: nullIfEmpty(moveTo),
      next_update_date: closing ? null : nullIfEmpty(nextUpdate),
      cost_zar: nullIfEmpty(cost),
      outcome: nullIfEmpty(outcome),
      assigned_person_id: nullIfEmpty(inCharge),
      notify,
    }
    if (op) update.operational = op

    try {
      const result = (await add.trigger({
        incident_id: incident.incident_id,
        update,
      }).result) as { notification?: { recipient?: string | null; queued?: boolean } | null } | null
      const recipient = result?.notification?.queued ? result.notification?.recipient : null
      toast.success('Update added', {
        description: recipient ? `Update emailed to ${recipient}.` : 'Recorded. No email was sent.',
      })
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not add the update', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) reset()
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
          <LayoutList className="h-3.5 w-3.5" />
          Card
          <span className="tabular-nums opacity-70">{card.incidents_total}</span>
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono uppercase">{card.registration_number || 'Vehicle'}</DialogTitle>
          {/* Wraps, unlike a normal chip: an offline reason can run to three
              clauses, and a chip that will not wrap stretches the whole dialog. */}
          <Chip tone={card.online ? 'green' : 'red'} className="w-fit whitespace-normal">
            {onlineLabel(card)}
          </Chip>
          <DialogDescription>
            {[card.make, card.model].filter(Boolean).join(' ') || 'Unknown vehicle'}
            {card.owner_name ? `, owned by ${card.owner_name}` : ''}.{' '}
            {card.incidents_total} incident{card.incidents_total === 1 ? '' : 's'} on this card,{' '}
            {card.open_count === 0 ? 'none open' : `${card.open_count} open`}.
          </DialogDescription>
        </DialogHeader>

        {/* The timeline, oldest first. The order and every judgement in it come
            from v_incident_timeline; this only draws it. */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Timeline</Label>
          {feed.loading && timeline.length === 0 ? (
            <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" /> Loading the timeline…
            </div>
          ) : feed.error ? (
            <p className="py-2 text-xs text-destructive">
              The timeline could not be loaded. Close this and open it again; if it keeps failing,
              the updates are still safe and nothing has been charged because of this.
            </p>
          ) : (
            <ol className="space-y-3 border-l border-border pl-4">
              {timeline.map((r, i) => (
                <TimelineEntry key={`${r.kind}-${r.at}-${i}`} row={r} />
              ))}
            </ol>
          )}
        </div>

        {canEdit && !incident && (
          <p className="border-t border-border pt-4 text-xs text-muted-foreground">
            Nothing is open on this car. Log a new incident to open one: it goes to the
            owner as its own report and appears on this card.
          </p>
        )}

        {canEdit && incident && (
        <div className="grid gap-4 border-t border-border pt-4 sm:grid-cols-2">
          {sortedOpen.length > 1 && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="iu-target">This update is for</Label>
              <NativeSelect
                id="iu-target"
                value={incident.incident_id}
                onChange={(e) => {
                  setTargetId(e.target.value)
                  const next = sortedOpen.find((r) => r.incident_id === e.target.value)
                  if (next) setStatus(next.status)
                }}
              >
                {sortedOpen.map((r) => (
                  <option key={r.incident_id} value={r.incident_id}>
                    {r.title || 'Incident'}, reported {dayLabel(r.occurred_at ?? r.reported_at)}
                  </option>
                ))}
              </NativeSelect>
              <p className="text-[11px] text-muted-foreground">
                Each open incident keeps its own next update date and person in charge.
              </p>
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="iu-note">
              What changed <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="iu-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Assessor attended, quote is R21 400, parts on order"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="iu-status">Status</Label>
            <NativeSelect id="iu-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {INCIDENT_STATUSES.map((s) => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </NativeSelect>
          </div>

          <TriState
            id="iu-operational"
            label="Vehicle operational"
            value={operational}
            onChange={setOperational}
          />

          {/* A SELECT, NOT A DATALIST. These two fields write straight onto
              vehicles.current_location through add_incident_update_v1, and a
              datalist suggests the list while accepting anything typed over it.
              On 1 September 2026, hours after migration 0126 cleaned the last free
              text out of that column, two cars came back through here carrying
              "Vehicle is operating" and "On Fleet Johannesburg". The first of
              those meant Operating, and the car read inactive because the string
              did not match. */}
          <div className="space-y-1.5">
            <Label htmlFor="iu-curloc">Current location</Label>
            <NativeSelect id="iu-curloc" value={currentLocation} onChange={(e) => setCurrentLocation(e.target.value)}>
              <option value="">No change</option>
              {LOCATION_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              {currentLocation !== '' && !LOCATION_OPTIONS.includes(currentLocation) && (
                <option value={currentLocation}>{currentLocation} (no longer offered)</option>
              )}
            </NativeSelect>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="iu-moveto">Move to</Label>
            <NativeSelect id="iu-moveto" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              <option value="">Only if it is being moved</option>
              {LOCATION_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </NativeSelect>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="iu-outcome">Outcome</Label>
            <Input id="iu-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="iu-cost">Cost to date (ZAR)</Label>
            <Input id="iu-cost" type="number" value={cost} onChange={(e) => setCost(e.target.value)} />
          </div>
          {!closing && (
            <div className="space-y-1.5">
              <Label htmlFor="iu-next">
                Next update by <span className="text-destructive">*</span>
              </Label>
              <Input
                id="iu-next"
                type="date"
                value={nextUpdate}
                min={bounds?.min}
                max={bounds?.max}
                onChange={(e) => setNextUpdate(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                {dateOutOfRange && bounds
                  ? `Choose a date from ${dayLabel(bounds.min)} to ${dayLabel(bounds.max)}.`
                  : `No more than ${bounds?.maxDays ?? 7} days away.`}
              </p>
            </div>
          )}

          {!closing && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="iu-incharge">
                Person in charge {needsInCharge && <span className="text-destructive">*</span>}
              </Label>
              <NativeSelect id="iu-incharge" value={inCharge} onChange={(e) => setInCharge(e.target.value)}>
                <option value="">
                  {incident.in_charge_name ? `No change (${incident.in_charge_name})` : 'Choose who is in charge'}
                </option>
                {people.map((p) => (
                  <option key={p.person_id} value={p.person_id}>{p.name}</option>
                ))}
              </NativeSelect>
              <p className="text-[11px] text-muted-foreground">
                They owe the next update. If it is not posted by the date above, a
                R50 Sin Offering is raised against them for approval.
              </p>
            </div>
          )}

          <div className="sm:col-span-2">
            <NotifyBox
              label="Send this update to the vehicle owner"
              checked={notify}
              onCheckedChange={setNotify}
              ownerWillBeNotified={ownerWillBeNotified}
              ownerName={incident.owner_name}
              hasVehicle
              internalReason={incident.incidents_internal_reason}
            />
          </div>
        </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={add.loading}>
            Close
          </Button>
          {canEdit && incident && (
            <Button onClick={handleSave} disabled={!canSave}>
              {add.loading && <Spinner className="h-4 w-4" />}
              Add update
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** One step on the timeline. Everything it says is decided in the view. */
function TimelineEntry({ row }: { row: TimelineRow }) {
  if (row.kind === 'due') {
    return (
      <li className="relative text-xs">
        <span
          className={`absolute -left-[21px] top-0.5 flex h-3 w-3 items-center justify-center rounded-full ${
            row.overdue || !row.person_name ? 'bg-destructive' : 'bg-[hsl(var(--brand))]'
          }`}
        />
        <div
          className={`rounded-md border p-2 ${
            row.overdue || !row.person_name
              ? 'border-destructive/30 bg-destructive/10 text-destructive'
              : 'border-border bg-muted/40 text-foreground'
          }`}
        >
          <p className="flex items-center gap-1.5 font-semibold">
            {row.overdue ? <CircleAlert className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
            {row.overdue ? 'Update overdue' : `Next update by ${dayLabel(row.on_date)}`}
          </p>
          {row.incident_title && <p className="mt-0.5 font-medium">{row.incident_title}</p>}
          <p className="mt-0.5">{dueSentence(row)}</p>
        </div>
      </li>
    )
  }

  if (row.kind === 'status') {
    const up = row.status_after === 'active'
    return (
      <li className="relative text-xs">
        <span className={`absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background ${up ? 'bg-emerald-500' : 'bg-destructive'}`} />
        <p className={`flex items-center gap-1.5 font-semibold ${up ? 'text-emerald-700 dark:text-emerald-400' : 'text-destructive'}`}>
          {up ? <Power className="h-3.5 w-3.5" /> : <PowerOff className="h-3.5 w-3.5" />}
          {entryHeading(row)}
          <span className="font-normal text-muted-foreground">{dayLabel(row.on_date)}</span>
        </p>
        {(row.location || row.body) && (
          <p className="mt-0.5 text-muted-foreground">{[row.location, row.body].filter(Boolean).join('. ')}</p>
        )}
      </li>
    )
  }

  const timing = row.kind === 'update' ? updateTimingSentence(row) : null
  return (
    <li className="relative text-xs">
      <span
        className={`absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background ${
          row.kind === 'offering' ? 'bg-destructive' : row.kind === 'reported' ? 'bg-foreground' : 'bg-muted-foreground'
        }`}
      />
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-semibold text-foreground">{entryHeading(row)}</span>
        {row.incident_title && row.kind !== 'offering' && (
          <span className="text-[11px] font-medium text-foreground">{row.incident_title}</span>
        )}
        <span className="text-[11px] text-muted-foreground">{dayLabel(row.on_date)}</span>
        {row.kind === 'reported' && row.merged && <Chip tone="neutral">Merged into this card</Chip>}
        {row.kind !== 'offering' && row.actor_name && (
          <span className="text-[11px] text-muted-foreground">by {row.actor_name}</span>
        )}
      </div>
      {row.kind === 'offering' ? (
        <p className="mt-0.5 text-destructive">{offeringSentence(row)}</p>
      ) : (
        row.body && <p className="mt-0.5 whitespace-pre-wrap text-foreground">{row.body}</p>
      )}
      {row.kind === 'update' && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
          {row.status_before !== row.status_after && (
            <Chip tone="neutral">
              {incidentStatusLabel(row.status_before)} to {incidentStatusLabel(row.status_after)}
            </Chip>
          )}
          {row.person_name && <span>In charge: {row.person_name}</span>}
          {row.location && <span>{row.location}</span>}
          {row.cost_zar !== null && <span>{zar(Number(row.cost_zar))}</span>}
          {row.owner_notified && (
            <span className="inline-flex items-center gap-1">
              <Mail className="h-3 w-3" /> emailed
            </span>
          )}
        </div>
      )}
      {timing && (
        <p className={`mt-0.5 text-[11px] ${row.on_time === false ? 'text-destructive' : 'text-muted-foreground'}`}>
          {timing}
        </p>
      )}
    </li>
  )
}
